/**
 * Durations in ms and easing as cubic-bezier control points — both are usable as-is by web CSS
 * (`transition: all {duration}ms cubic-bezier(...)`) and by RN's `Animated`/`Reanimated`
 * (`Easing.bezier(x1, y1, x2, y2)`, `duration: {duration}`).
 *
 * V2 motion philosophy: fast, professional, purposeful. Micro-interactions (hover, press) are near
 * instant; entrances use a gentle decelerate; the `emphasized` curve gives hero/dialog motion a
 * premium, slightly-overshooting settle. Nothing here should ever delay a user completing a task.
 */
export const duration = {
  instant: 100,
  short: 150,
  medium: 220,
  long: 320,
  slow: 480,
} as const;

export const easing = {
  standard: [0.4, 0.0, 0.2, 1] as const,
  decelerate: [0.0, 0.0, 0.2, 1] as const,
  accelerate: [0.4, 0.0, 1, 1] as const,
  /** Premium settle — slight follow-through, for dialogs, hero cards, snackbars. */
  emphasized: [0.2, 0.0, 0, 1] as const,
  /** Springy pop for badges/toasts. */
  spring: [0.34, 1.56, 0.64, 1] as const,
} as const;

/** Named presets for the common cases — a component spec says `transition: transitions.fade`. */
export const transitions = {
  fade: { duration: duration.short, easing: easing.standard },
  slideIn: { duration: duration.medium, easing: easing.decelerate },
  slideOut: { duration: duration.short, easing: easing.accelerate },
  collapse: { duration: duration.medium, easing: easing.standard },
  /** Hover/press micro-interactions on buttons, cards, list rows. */
  hover: { duration: duration.short, easing: easing.standard },
  press: { duration: duration.instant, easing: easing.accelerate },
  /** Card / dialog entrance with a premium settle. */
  pop: { duration: duration.long, easing: easing.emphasized },
  /** Page-level transition. */
  page: { duration: duration.medium, easing: easing.decelerate },
} as const;

export type DurationKey = keyof typeof duration;
export type EasingKey = keyof typeof easing;
export type TransitionKey = keyof typeof transitions;
